import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  canAssignRoles,
  canManageLabels,
  canReviewAccessRequests,
  canSeeInventory,
  type AppRole,
} from '@infras/shared-types';
import { CrumbProvider } from '../lib/crumb';
import { DOC_SECTIONS, docParent } from '../lib/docs';
import { ASTRYX_TRIAL } from '../lib/trial';
import { useFreshness } from '../lib/useFreshness';
import { Icon, type IconName } from './Icon';
import { PendingAccessBadge } from './PendingAccessBadge';
import { TopBar } from './TopBar';

/** Sidebar counts — see the `navCounts` resolver for why this isn't the list queries. */
const NAV_COUNTS = gql`
  query NavCounts {
    navCounts {
      repos
      clusters
      secrets
    }
  }
`;

const COLLAPSE_KEY = 'infras.sidebar';
/** Which docs groups (SecureLink, TransForge) the user has opened. A NEW key, not a
 *  reuse of anything older: nothing was stored for these groups before, so every
 *  user starts collapsed and only their own toggle is remembered after that. */
const DOC_GROUPS_KEY = 'infras.sidebar.docGroups.v1';

function readDocGroups(): Record<string, boolean> {
  try {
    const raw = JSON.parse(localStorage.getItem(DOC_GROUPS_KEY) ?? '{}');
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function writeDocGroups(v: Record<string, boolean>) {
  try {
    localStorage.setItem(DOC_GROUPS_KEY, JSON.stringify(v));
  } catch {
    /* storage blocked — the toggle still works for this page load */
  }
}

/** The docs sections that have subpages — the collapsible groups. */
const DOC_GROUP_SLUGS: string[] = DOC_SECTIONS.filter((s) => DOC_SECTIONS.some((c) => docParent(c.slug) === s.slug)).map(
  (s) => s.slug,
);

/** The group a docs route belongs to: the section itself or its parent. */
function docGroupOf(pathname: string): string | undefined {
  if (!pathname.startsWith('/docs/')) return undefined;
  const slug = pathname.split('/')[2];
  const g = docParent(slug) ?? slug;
  return DOC_GROUP_SLUGS.includes(g) ? g : undefined;
}

type Counts = { repos: number; clusters: number; secrets: number };

interface NavItem {
  to: string;
  label: string;
  /** Sidebar icon. Replaces the 2-char monogram the first cut used because the app
   *  had no icon set; `Icon` now provides one with no dependency. */
  icon: IconName;
  /** Which count to show, if any. */
  count?: keyof Counts;
  /** Renders its own trailing element (pending badge, freshness dot). */
  slot?: 'pending' | 'freshness';
  /** Show DOC_SECTIONS beneath this item while the route is active. `DocsPage`
   *  renders no section menu of its own — it documents that the menu lives in
   *  this sidebar — so dropping this makes all four doc sections URL-only. */
  docsSubnav?: true;
  /** Paths under `to` that belong to another nav item, so this one is not marked active there. */
  notActiveUnder?: string[];
  can: (role: AppRole) => boolean;
}

const EVERYONE = () => true;

/** The path belongs to another item nested under this one (e.g. `/tasks/board`). */
const underOther = (item: NavItem, pathname: string) =>
  !!item.notActiveUnder?.some((p) => pathname === p || pathname.startsWith(p + '/'));

/**
 * The nav, grouped. Gates are the SAME predicates the API enforces
 * (`packages/shared-types/src/rbac.ts`) — this is UX; RolesGuard is the boundary.
 *
 * The four groups come from the redesign handoff. **Network is kept as a fifth
 * group**: the handoff's table omitted Atlas IPs, Agents and the three VPN views,
 * and rendering it literally would have left five live routes reachable only by
 * typing the URL. Atlas IPs + Agents fold into Operations for the same reason.
 *
 * ORDER IS DELIBERATE and is what renders. Reordering this array reorders the
 * sidebar — there is no separate sort.
 *
 *  - The FIRST group has an empty label and renders as a pinned block above a
 *    separator. Tasks and Docs & MCP are the only two items every role
 *    can open, and they used to sit inside Operations and Reference — so a
 *    `new_member`, who is this app's main filer of access tasks, got a sidebar
 *    reading "OPERATIONS" with one item under it and "REFERENCE" with one under
 *    that. Two group labels out of the app's internal vocabulary, wrapped around
 *    everything they are allowed to do. Unlabelled, they read as "things you can
 *    always do", which is what they are. They stay first for every other role
 *    too: Tasks is the most-used cross-role surface in the app.
 *  - Tasks (2026-10) is the second group: one Tasks list for every role (access,
 *    change and blank are templates of one task type) carrying the pending-access
 *    badge (`pendingAccessCount`), and the weekly Board for engineers and above. A
 *    new member sees Tasks alone.
 *  - Administration sits LAST. It is the group the fewest people can open and
 *    the one nobody visits to do their actual work.
 */
const NAV: { group: string; testid?: string; items: NavItem[] }[] = [
  {
    group: '',
    items: [
      { to: '/docs', label: 'Docs & MCP', icon: 'docs', docsSubnav: true, can: EVERYONE },
    ],
  },
  {
    // One list for every role; the board is engineer and above (the API refuses
    // new members). `/tasks/board` sits under `/tasks`, so the list item must not
    // light up while the board is open. The testid keeps its old name (QC specs).
    group: 'Tasks',
    testid: 'nav-requests-group',
    items: [
      {
        to: '/tasks',
        label: 'Tasks',
        icon: 'inbox',
        slot: 'pending',
        notActiveUnder: ['/tasks/board'],
        can: EVERYONE,
      },
      { to: '/tasks/board', label: 'Board', icon: 'grid', can: canSeeInventory },
    ],
  },
  {
    group: 'Inventory',
    items: [
      // `/overview` had a route and a crumb but no nav entry, so it was reachable
      // only by clicking the brand — which is not a thing anyone discovers. It is
      // the page that answers "can I trust what I'm about to read", so it belongs
      // at the top of the group rather than hidden behind the logo.
      { to: '/overview', label: 'Overview', icon: 'grid', can: canSeeInventory },
      { to: '/repos', label: 'Repos', icon: 'repo', count: 'repos', can: canSeeInventory },
      { to: '/clusters', label: 'Clusters', icon: 'cluster', count: 'clusters', can: canSeeInventory },
      { to: '/alerts', label: 'Alerts', icon: 'alert', can: canSeeInventory },
      { to: '/secrets', label: 'Secrets', icon: 'key', count: 'secrets', can: canSeeInventory },
    ],
  },
  {
    group: 'Operations',
    items: [
      { to: '/scheduler', label: 'Scheduler', icon: 'clock', slot: 'freshness', can: canSeeInventory },
      { to: '/activity', label: 'Activity', icon: 'monitor', can: canSeeInventory },
      { to: '/incident', label: 'Notify owners', icon: 'alert', can: canSeeInventory },
      // Cost is counts-and-states, not credentials — the ordinary inventory gate,
      // matching the resolver, which carries no @RequireRole. An engineer who owns a
      // service is the person who should see what it costs.
      { to: '/costs/cdn', label: 'CDN cost', icon: 'catalog', can: canSeeInventory },
      // ONE item, not two. "Atlas IPs" and "Agents" were two menu entries over one
      // mechanism — the entry states are classified from the agent heartbeats — and
      // the owner's report was that the split "makes me confuse". The name is the
      // QUESTION the screen answers rather than either implementation.
      { to: '/cluster-access', label: 'Cluster access', icon: 'shield', can: canSeeInventory },
    ],
  },
  {
    // Deploy catalog is alone here now that Docs is pinned. Kept as its own group
    // rather than folded into Inventory because it is not scanned truth — it is
    // the hand-maintained recipe of record, and filing it under Inventory would
    // imply a scanner stands behind it.
    group: 'Reference',
    items: [
      { to: '/catalog', label: 'Deploy catalog', icon: 'catalog', can: canSeeInventory },
      // EVERYONE, unlike the rest of Reference. This page is a vocabulary — error
      // codes and the customer copy that goes with each — not inventory. A new
      // member fielding their first ticket is exactly who needs to look a code up,
      // and gating it behind a role assignment is a worse failure than showing it.
      { to: '/error-codes', label: 'Error dictionary', icon: 'docs', can: EVERYONE },
    ],
  },
  {
    group: 'Network',
    items: [
      { to: '/vpn/peers', label: 'Peers', icon: 'account', can: canSeeInventory },
      // Astryx trial — QA and local builds only; the entry is not in other bundles (lib/trial.ts).
      ...(ASTRYX_TRIAL
        ? ([{ to: '/vpn/peers-astryx', label: 'Peers (Astryx)', icon: 'account', can: canSeeInventory }] satisfies NavItem[])
        : []),
      { to: '/vpn/subnets', label: 'Subnets', icon: 'cluster', can: canSeeInventory },
      // SOC2 audit-of-record — root only, matching @RequireRole('root') on the query.
      { to: '/vpn/connection-audit', label: 'Connection audit', icon: 'monitor', can: canAssignRoles },
    ],
  },
  {
    group: 'Administration',
    items: [
      { to: '/settings/team', label: 'Users & roles', icon: 'users', can: canAssignRoles },
      {
        to: '/settings/reviewers',
        // Short in the sidebar (the full name truncated at its width); the page and
        // crumb say "Default access assignees".
        label: 'Access assignees',
        icon: 'shield',
        can: canReviewAccessRequests,
      },
      {
        to: '/settings/questions',
        label: 'Access questions',
        icon: 'question',
        can: canReviewAccessRequests,
      },
      { to: '/settings/labels', label: 'Labels', icon: 'catalog', can: canManageLabels },
    ],
  },
];

export function AppShell({
  role,
  email,
  onLogout,
  children,
}: {
  role: AppRole;
  email: string;
  onLogout: () => void;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');
  const { pathname } = useLocation();
  // Default collapsed. The group holding the open page starts (and, on navigation,
  // becomes) expanded so the active item is visible; that auto-open is not
  // persisted — only the user's own toggle is.
  const [docGroups, setDocGroups] = useState<Record<string, boolean>>(() => {
    const stored = readDocGroups();
    const g = docGroupOf(pathname);
    return g ? { ...stored, [g]: true } : stored;
  });
  useEffect(() => {
    const g = docGroupOf(pathname);
    if (g) setDocGroups((prev) => (prev[g] ? prev : { ...prev, [g]: true }));
  }, [pathname]);
  const toggleDocGroup = (slug: string) => {
    const next = { ...docGroups, [slug]: !docGroups[slug] };
    // Persist only explicit choices (not the auto-opened group for the current route).
    const stored = readDocGroups();
    writeDocGroups({ ...stored, [slug]: next[slug] });
    setDocGroups(next);
  };
  const inDocs = pathname.startsWith('/docs');
  // The docs section whose subpage is open, so the sidebar can mark it as the way in.
  const openDocParent = inDocs ? docParent(pathname.split('/')[2]) : undefined;
  const inventory = canSeeInventory(role);
  const freshness = useFreshness(!inventory);
  const { data } = useQuery<{ navCounts: Counts }>(NAV_COUNTS, { skip: !inventory });
  const counts = data?.navCounts;

  // Persist OUTSIDE the updater — React may invoke an updater more than once
  // (StrictMode does in dev) and requires it to be pure.
  const toggle = () => {
    const next = !collapsed;
    localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
    setCollapsed(next);
  };

  return (
    <CrumbProvider>
      <div className={`app-shell${collapsed ? ' app-shell--collapsed' : ''}`}>
        <TopBar
          email={email}
          role={role}
          onLogout={onLogout}
          showInventory={inventory}
        />
        <div className="app-body">
          <aside className="sidebar" aria-label="Main navigation">
            <nav className="side-nav">
              {NAV.map(({ group, testid, items }) => {
                const visible = items.filter((i) => i.can(role));
                if (!visible.length) return null;
                return (
                  // An empty group name is the pinned block: no label, and a rule
                  // below it so it reads as pinned rather than as the head of
                  // whatever group follows.
                  <div
                    className={`nav-group${group ? '' : ' nav-group--pinned'}`}
                    key={group}
                    data-testid={testid}
                  >
                    {group && <div className="nav-label">{group}</div>}
                    {visible.map((item) => (
                      <div key={item.to}>
                        <NavLink
                          to={item.to}
                          // `end` only on '/', or every route would mark Overview active
                          end={item.to === '/'}
                          className={({ isActive }) =>
                            isActive && !underOther(item, pathname) ? 'nav-item active' : 'nav-item'
                          }
                          aria-current={underOther(item, pathname) ? 'false' : 'page'}
                          title={collapsed ? item.label : undefined}
                          // The icon is aria-hidden, so the collapsed rail needs
                          // the name here or a screen reader gets an empty link.
                          aria-label={collapsed ? item.label : undefined}
                        >
                          <Icon name={item.icon} className="nav-icon" />
                          <span className="nav-text">{item.label}</span>
                          {item.count && counts && (
                            <span className="nav-count">{counts[item.count]}</span>
                          )}
                          {/* Not gated on `inventory` any more: the badge counts
                              your OWN pending access tasks too, and a new member with
                              one in flight is exactly who needs to see it. */}
                          {item.slot === 'pending' && <PendingAccessBadge />}
                          {item.slot === 'freshness' && (
                            <span
                              className={`freshness-dot freshness-dot--${freshness.tone}`}
                              title={freshness.label}
                            />
                          )}
                        </NavLink>
                        {item.docsSubnav && inDocs && !collapsed && (
                          <div className="side-subnav">
                            {DOC_SECTIONS.map((s) => {
                              const parent = docParent(s.slug);
                              // A subpage is hidden while its group is collapsed.
                              if (parent && !docGroups[parent]) return null;
                              const isGroup = DOC_GROUP_SLUGS.includes(s.slug);
                              const link = (
                                <NavLink
                                  key={s.slug}
                                  to={`/docs/${s.slug}`}
                                  className={`side-subnav-link${parent ? ' side-subnav-child' : ''}${
                                    s.slug === openDocParent ? ' has-open-child' : ''
                                  }`}
                                >
                                  {s.label}
                                </NavLink>
                              );
                              if (!isGroup) return link;
                              const open = !!docGroups[s.slug];
                              return (
                                <div className="side-subnav-row" key={s.slug} data-testid={`nav-docs-group-${s.slug}`}>
                                  {link}
                                  <button
                                    type="button"
                                    className="side-subnav-toggle"
                                    aria-expanded={open}
                                    aria-label={`${open ? 'Collapse' : 'Expand'} ${s.label}`}
                                    onClick={() => toggleDocGroup(s.slug)}
                                  >
                                    <Icon
                                      name="chevron"
                                      className={`collapse-chevron${open ? ' collapse-chevron--down' : ''}`}
                                    />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </nav>
            <div className="sidebar-foot">
              <button
                className="sidebar-collapse"
                onClick={toggle}
                aria-expanded={!collapsed}
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <Icon name="chevron" className={`collapse-chevron${collapsed ? '' : ' collapse-chevron--flip'}`} />
                <span className="nav-text">Collapse</span>
              </button>
            </div>
          </aside>
          <main className="app-main">{children}</main>
        </div>
      </div>
    </CrumbProvider>
  );
}
